import { Button, Col, Modal, Row } from "antd";
import { ModalProps } from "antd-v5";
import classnames from "classnames";
import _ from "lodash";
import React, {
  ReactNode,
  Ref,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
} from "react";
import ModalUtil from "utils/modalUtil";

import AuthorisedContainer from "./AuthorisedContainer";
import WarningPopup from "./WarningPopup";

export type OkButtonProps = {
  permission?: string;
  disabled?: boolean;
};

export interface InternalModalProps extends Omit<ModalProps, "onCancel"> {
  title?: React.ReactNode;
  onOk?: () => Promise<void> | void;
  onCancel?: (closeModal: () => void) => boolean | void;
  okText?: string;
  className?: string;
  footer?: ReactNode;
  okButtonProps?: OkButtonProps;
  cancelBtn?: boolean;
  cancelBtnText?: string;
  disabled?: boolean;
}

export interface BaseModalProps extends ModalProps {
  modalProps?: InternalModalProps;
  children?: ReactNode;
  afterClose?: (response?: any) => void;
  showExitWarning?: boolean;
  secondaryButton?: ReactNode;
}

export interface BaseModalRef {
  close: () => void;
}

const BaseModal = forwardRef(
  (
    {
      modalProps = {},
      children,
      afterClose,
      showExitWarning,
      secondaryButton,
    }: BaseModalProps,
    ref: Ref<BaseModalRef>,
  ) => {
    const [visible, setVisible] = useState(true);
    const [loading, setLoading] = useState(false);

    const { onCancel: propsOnCancel } = modalProps;

    useImperativeHandle(ref, () => ({
      close: () => setVisible(false),
    }));

    const onOk = async () => {
      setLoading(true);
      try {
        await (modalProps.onOk || _.noop)();
        setVisible(false);
      } catch (e) {
        setLoading(false);
        console.log(e);
      }
    };

    const exitModal = useCallback(() => {
      let popupControl = false;
      const closeModal = () => setVisible(false);
      if (propsOnCancel) {
        popupControl = !!propsOnCancel(closeModal);
      }
      if (!popupControl) {
        closeModal();
      }
    }, [propsOnCancel]);

    const onCancel = useCallback(() => {
      if (showExitWarning) {
        ModalUtil.show({
          content: (
            <WarningPopup
              //@ts-expect-error : should accept promptMessage prop
              promptMessage={"Any unsaved changes will be lost."}
              okText="Proceed"
              onOk={exitModal}
            />
          ),
        });
      } else {
        exitModal();
      }
    }, [showExitWarning, exitModal]);

    const _modalProps = {
      ...modalProps,
      footer: null,
    };
    const okText = modalProps.okText || "Save";

    return (
      // @ts-expect-error: Closable is boolean at runtime.
      <Modal
        {..._modalProps}
        className={classnames(modalProps.className, "base-modal")}
        afterClose={afterClose}
        visible={visible}
        maskClosable={false}
        onCancel={onCancel}
        getContainer={() => {
          const el = document.getElementsByClassName("app-content-wrapper")[0];
          // Make sure it's an HTMLElement, otherwise fallback to document.body
          return el instanceof HTMLElement ? el : document.body;
        }}
      >
        {children}
        {_.has(modalProps, "footer") ? (
          modalProps.footer
        ) : (
          <Row className="m-t-m m-b-m">
            <Col span={24}>
              {modalProps.okButtonProps?.permission ? (
                <AuthorisedContainer
                  permission={modalProps.okButtonProps.permission}
                >
                  <Button
                    className="pull-right"
                    loading={loading}
                    htmlType="submit"
                    type="primary"
                    onClick={onOk}
                    {...modalProps.okButtonProps}
                  >
                    {okText}
                  </Button>
                </AuthorisedContainer>
              ) : (
                <Button
                  className="pull-right"
                  loading={loading}
                  htmlType="submit"
                  type="primary"
                  disabled={modalProps.disabled}
                  onClick={onOk}
                  {...modalProps.okButtonProps}
                >
                  {okText}
                </Button>
              )}
              {secondaryButton}
              {_modalProps.cancelBtn !== false && (
                <Button
                  type="link"
                  className="pull-right m-r-xs p-r-xs"
                  onClick={onCancel}
                >
                  {modalProps.cancelBtnText || "Cancel"}
                </Button>
              )}
            </Col>
          </Row>
        )}
      </Modal>
    );
  },
);

export default BaseModal;
